import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Bell } from 'lucide-react';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/notifications?limit=15');
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch (_e) {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(id); document.removeEventListener('mousedown', onClick); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      // optimistic
      setUnread(0);
      try { await api.post('/notifications/read'); load(); } catch (_e) {}
    }
  };

  const iconFor = (n) => {
    if (n.kind === 'milestone_nft') return '💎';
    if (n.kind === 'chat_reaction') return n.emoji || '🚀';
    return '🔔';
  };

  const linkFor = (n) => {
    if (n.kind === 'chat_reaction' || n.kind === 'milestone_nft') return '/referrals';
    return '/dashboard';
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative p-2 rounded-sm hover:bg-[#2B3139] text-[#EAECEF]"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-4.5 h-4.5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#F6465D] text-white text-[10px] font-mono flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] bg-[#181A20] border border-[#2B3139] rounded-sm shadow-xl z-50 flex flex-col" data-testid="notification-dropdown">
          <div className="px-3 py-2.5 border-b border-[#2B3139] flex items-center justify-between">
            <div className="text-white font-semibold text-sm">Notifications</div>
            <span className="text-xs text-[#5E6673] font-mono">{items.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#5E6673] italic">No notifications yet.</div>
            ) : items.map(n => (
              <Link to={linkFor(n)} onClick={() => setOpen(false)}
                key={n.notification_id}
                className={`flex items-start gap-2 px-3 py-2.5 border-b border-[#2B3139]/60 hover:bg-[#0B0E11] ${n.read ? '' : 'bg-[#FCD535]/5'}`}
                data-testid={`notif-${n.notification_id}`}
              >
                <div className="w-8 h-8 grid place-items-center bg-[#0B0E11] border border-[#2B3139] rounded-sm flex-shrink-0 text-lg leading-none">
                  {iconFor(n)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#EAECEF] leading-snug">
                    {n.kind === 'chat_reaction' && (
                      <><span className="font-semibold">{n.from_user_name}</span> reacted {n.emoji} to your message</>
                    )}
                    {n.kind === 'milestone_nft' && (
                      <><span className="font-semibold">Milestone NFT</span> minted for your group</>
                    )}
                  </div>
                  {n.preview && <div className="text-xs text-[#848E9C] mt-0.5 truncate">"{n.preview}"</div>}
                  <div className="text-[10px] text-[#5E6673] font-mono mt-0.5">
                    {new Date(n.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {new Date(n.at).toLocaleDateString()}
                  </div>
                </div>
                {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#FCD535] mt-2 flex-shrink-0" />}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
